import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Sparkles, TrendingUp, Lock, Car, Palette, Wine, Gem, ShoppingBag, Watch, Smartphone, Download, Share, Monitor, ShieldCheck, Warehouse, Fingerprint, Layers, Globe, CreditCard, Wallet as Wallet2, AlertCircle } from 'lucide-react';
import { FEATURES } from '../config/features';

const features = [
  {
    icon: ShieldCheck,
    title: 'Track',
    description: 'One encrypted vault for cars, art, watches, jewellery, wine and handbags. Valued and kept current.'
  },
  {
    icon: AlertCircle,
    title: 'Understand',
    description: 'A live Portfolio Health Score across coverage, diversification and exposure. What is underinsured, expiring, or over-concentrated, surfaced before it costs you.'
  },
  {
    icon: TrendingUp,
    title: 'Act',
    description: 'Every value change generates a recommended next step. Pre-filled outreach to your broker, advisor or insurer turns insight into a one-click fix.'
  },
];

const categories = [
  { icon: Car, label: 'Cars', image: 'https://customer-assets.emergentagent.com/job_a2138fcd-a34c-4239-b6a3-5ae6e23b6b2e/artifacts/og2c33wi_jeff-tumale-AWjAGG-u_eE-unsplash.jpg' },
  { icon: Palette, label: 'Fine Art', image: 'https://images.unsplash.com/photo-1551619276-f77b2c749711' },
  { icon: Wine, label: 'Wine & Spirits', image: 'https://customer-assets.emergentagent.com/job_a2138fcd-a34c-4239-b6a3-5ae6e23b6b2e/artifacts/qpyysfoa_nat-_N53MiIh4YA-unsplash.jpg' },
  { icon: Gem, label: 'Jewellery', image: 'https://customer-assets.emergentagent.com/job_a2138fcd-a34c-4239-b6a3-5ae6e23b6b2e/artifacts/73frbrwa_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2006_40_38%20PM.png' },
  { icon: ShoppingBag, label: 'Handbags', image: 'https://customer-assets.emergentagent.com/job_a2138fcd-a34c-4239-b6a3-5ae6e23b6b2e/artifacts/4gntfqmx_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2006_42_08%20PM.png' },
  { icon: Watch, label: 'Watches', image: 'https://images.unsplash.com/photo-1670404160620-a3a86428560e' },
];

export default function Landing() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/assagart-logo.png?v=2" 
              alt="Assagart" 
              className="h-14 w-auto"
            />
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="btn-primary" data-testid="header-dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-gold" data-testid="header-login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary" data-testid="header-register-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1627761801957-4bf6cfb4fa20"
            alt="Luxury yacht"
            className="w-full h-full object-cover opacity-30"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-gold uppercase tracking-[0.3em] text-xs mb-6 animate-fade-in">
            The Private Asset Vault
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white mb-6 animate-slide-up leading-tight">
            Every asset you own,<br className="hidden sm:inline" /> in one private vault.
          </h1>
          <p className="text-base text-muted-foreground tracking-[0.3em] uppercase mb-10 animate-slide-up stagger-2">
            Track. Understand. Act.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up stagger-3">
            <Link to="/register">
              <Button className="btn-primary flex items-center gap-2" data-testid="hero-cta-btn">
                Begin Your Journey
                <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/login">
              <Button className="btn-secondary" data-testid="hero-login-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-white/20 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-gold rounded-full" />
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Asset Categories</p>
            <h2 className="font-serif text-4xl text-white">Every Asset, One Vault</h2>
          </div>
          
          {/* All 6 categories in centered grid */}
          <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
            {categories.map((cat, index) => {
              const Icon = cat.icon;
              return (
                <div 
                  key={cat.label}
                  className="group relative aspect-square overflow-hidden luxury-card p-0 cursor-pointer w-[calc(50%-0.5rem)] md:w-[calc(25%-0.75rem)]"
                  data-testid={`category-${cat.label.toLowerCase().replace(' ', '-')}`}
                >
                  <img 
                    src={cat.image} 
                    alt={cat.label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Icon size={28} className="text-gold mb-2" />
                    <span className="text-white text-sm tracking-wider">{cat.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section - the V → R → A flow that the hero alludes to */}
      <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">How it works</p>
            <h2 className="font-serif text-4xl text-white">From value to action - automatically.</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting lines between pillars on desktop */}
            <div className="hidden md:block absolute top-[44px] left-[16.6%] right-[16.6%] h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  className="luxury-card p-8 text-center relative"
                  data-testid={`feature-${feature.title.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="w-12 h-12 mx-auto mb-6 border border-gold/30 flex items-center justify-center bg-[#0A0A0A] relative z-10">
                    <Icon size={24} className="text-gold" />
                  </div>
                  <p className="text-[10px] text-gold/70 uppercase tracking-[0.25em] mb-2">Step {index + 1}</p>
                  <h3 className="font-serif text-xl text-white mb-3">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Digital Twin Vault Teaser - hidden behind feature flag */}
      {FEATURES.TOKENISATION_ENABLED && (
      <section className="py-24 px-6 lg:px-12 relative overflow-hidden" data-testid="digital-twin-teaser">
        {/* Background accent */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold/[0.03] rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Introducing</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-4">The Digital Twin Vault</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base leading-relaxed">
              Where physical luxury meets blockchain permanence. Transform your most prized possessions 
              into verified, on-chain Digital Twins, securing their provenance, value, and legacy forever.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Left: Explanation */}
            <div className="space-y-8">
              <div>
                <h3 className="font-serif text-2xl text-white mb-3">What is a Digital Twin?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A Digital Twin is a blockchain-secured digital representation of your physical asset. 
                  Think of it as an immutable certificate of authenticity, ownership history, and real-time valuation, 
                  all cryptographically sealed and verifiable by anyone, anywhere. Unlike traditional documentation, 
                  a Digital Twin cannot be forged, lost, or degraded over time.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                    <Fingerprint size={18} className="text-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Immutable Provenance</h4>
                    <p className="text-sm text-muted-foreground">Every ownership transfer, valuation, and condition report is permanently recorded on-chain. Your asset's story becomes unforgeable.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                    <Layers size={18} className="text-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Fractional Ownership</h4>
                    <p className="text-sm text-muted-foreground">Divide your asset into shareable fractions. Enable trusted co-ownership or raise liquidity without selling the physical piece.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-gold/30 flex items-center justify-center flex-shrink-0">
                    <Globe size={18} className="text-gold" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-1">Global Marketplace</h4>
                    <p className="text-sm text-muted-foreground">Access a curated marketplace of verified luxury assets. Buy and sell fractions with institutional-grade security and transparency.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Visual card */}
            <div className="relative">
              <div className="relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-hidden" style={{ boxShadow: '0 0 40px rgba(212, 175, 55, 0.15), 0 0 80px rgba(212, 175, 55, 0.05)' }}>
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a2138fcd-a34c-4239-b6a3-5ae6e23b6b2e/artifacts/73frbrwa_ChatGPT%20Image%20Apr%2029%2C%202026%2C%2006_40_38%20PM.png" 
                    alt="Cartier Juste un Clou Digital Twin"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur-sm border border-gold/30 rounded-full">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[10px] text-emerald-400 uppercase tracking-wider">Vault Active</span>
                  </div>
                  <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm border border-[#627EEA]/30 rounded-full">
                    <span className="text-[10px] text-[#627EEA] font-mono">#DT-00391</span>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <h4 className="text-white font-serif text-lg">Cartier Juste un Clou</h4>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">18k Yellow Gold · Bracelet · Ref. B6048517</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-[10px] mb-1">
                      <span className="text-muted-foreground uppercase tracking-wider">Ownership</span>
                      <span className="text-gold font-mono">100.0%</span>
                    </div>
                    <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-gold to-[#F4D03F]" style={{ width: '100%' }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded"><Shield size={10} className="text-emerald-400" /><span className="text-[9px] text-emerald-400 uppercase">Insured</span></div>
                    <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded"><Sparkles size={10} className="text-gold" /><span className="text-[9px] text-gold uppercase">Authenticated</span></div>
                    <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded"><Lock size={10} className="text-[#627EEA]" /><span className="text-[9px] text-[#627EEA] uppercase">On-Chain</span></div>
                  </div>
                </div>
              </div>
              {/* Glow accents */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border border-gold/10 rounded-full" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-gold/5 rounded-full" />
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-muted-foreground text-sm mb-6">
              The Digital Twin Vault is an exclusive add-on available to Elite members by invitation.
            </p>
            <Link to="/register">
              <Button className="btn-primary" data-testid="dt-teaser-cta">
                Join Assagart
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Payments badge - was a 60-line compliance section, now a single discreet pill */}
      <section className="py-12 px-6 lg:px-12 border-t border-white/5" data-testid="stripe-payments-section">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
          <div className="flex items-center gap-2 px-4 py-2 border border-white/10 rounded-full bg-[#080808]">
            <Lock size={12} className="text-gold" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Payments by Stripe · PCI-DSS Level 1</span>
          </div>
          <p className="text-xs text-muted-foreground/70">
            Membership and renewals settle in GBP through Stripe's secure checkout.
          </p>
        </div>
      </section>
      {/* Install section - condensed: feature pills + collapsible install steps */}
      <section className="py-24 px-6 lg:px-12" data-testid="install-app-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Install App</p>
              <h2 className="font-serif text-4xl text-white mb-6">Add to Home Screen</h2>
              <p className="text-muted-foreground mb-8 leading-relaxed max-w-md">
                Install Assagart for native-app access, full-screen mode and offline portfolio data. No app store required.
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-full text-xs text-muted-foreground">
                  <Smartphone size={12} className="text-gold" /> Native feel
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-full text-xs text-muted-foreground">
                  <Sparkles size={12} className="text-gold" /> Offline access
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-full text-xs text-muted-foreground">
                  <Download size={12} className="text-gold" /> Instant updates
                </span>
              </div>

              <details className="group bg-white/5 border border-white/10 rounded">
                <summary className="cursor-pointer list-none p-4 flex items-center justify-between text-sm text-white hover:bg-white/[0.07]">
                  <span>How to install</span>
                  <ArrowRight size={14} className="text-gold transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-4 pb-4 grid sm:grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <p className="text-white font-medium mb-2 flex items-center gap-2"><Smartphone size={12} className="text-gold" /> iPhone / iPad</p>
                    <ol className="space-y-1">
                      <li>1. Tap <Share size={11} className="inline text-white" /> Share in Safari</li>
                      <li>2. "Add to Home Screen"</li>
                      <li>3. Tap "Add"</li>
                    </ol>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-2 flex items-center gap-2"><Monitor size={12} className="text-gold" /> Android</p>
                    <ol className="space-y-1">
                      <li>1. Tap the Chrome menu</li>
                      <li>2. "Install app" or "Add to Home screen"</li>
                      <li>3. Tap "Install"</li>
                    </ol>
                  </div>
                </div>
              </details>
            </div>

            {/* Phone mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <div className="w-[280px] h-[560px] bg-[#1a1a1a] rounded-[40px] p-3 shadow-2xl border border-white/10">
                  <div className="w-full h-full bg-[#050505] rounded-[32px] overflow-hidden relative">
                    <div className="h-8 bg-[#050505] flex items-center justify-center">
                      <div className="w-20 h-5 bg-black rounded-full" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-6">
                        <img
                          src="/assagart-logo.png?v=2"
                          alt="Assagart"
                          className="h-10 w-auto"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-[10px] text-gold uppercase tracking-widest mb-2">Portfolio Overview</p>
                      <h3 className="font-serif text-2xl text-white mb-4">Your Vault</h3>
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="bg-[#0a0a0a] border border-white/10 p-3">
                          <p className="text-[8px] text-muted-foreground mb-1">TOTAL VALUE</p>
                          <p className="text-gold font-semibold">£4.2M</p>
                        </div>
                        <div className="bg-[#0a0a0a] border border-white/10 p-3">
                          <p className="text-[8px] text-muted-foreground mb-1">GAIN</p>
                          <p className="text-green-400 font-semibold">+12.4%</p>
                        </div>
                      </div>
                      <div className="bg-[#0a0a0a] border border-white/10 p-3 flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
                          <Watch size={16} className="text-amber-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm">Patek Philippe</p>
                          <p className="text-[10px] text-muted-foreground">Watch · £185K</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-[#0a0a0a] border-t border-white/10 flex items-center justify-around px-4">
                      <div className="flex flex-col items-center">
                        <div className="w-1 h-1 bg-gold rounded-full mb-1" />
                        <span className="text-[8px] text-gold">Home</span>
                      </div>
                      <span className="text-[8px] text-muted-foreground">Assets</span>
                      <span className="text-[8px] text-muted-foreground">Risk</span>
                      <span className="text-[8px] text-muted-foreground">AI</span>
                      <span className="text-[8px] text-muted-foreground">Settings</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 border border-gold/20 rounded-full" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 border border-gold/10 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl text-white mb-6">
            Ready to Transform Your Portfolio?
          </h2>
          <p className="text-muted-foreground mb-10">
            Join discerning collectors who trust Assagart with their most prized possessions.
          </p>
          <Link to="/register">
            <Button className="btn-primary" data-testid="cta-register-btn">
              Create Your Vault
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img 
            src="/assagart-logo.png?v=2" 
            alt="Assagart" 
            className="h-12 w-auto"
            loading="lazy"
          />
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-gold transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/plans" className="hover:text-gold transition-colors" data-testid="footer-pricing">Pricing</Link>
            <a href="mailto:hello@assagart.co.uk" className="hover:text-gold transition-colors" data-testid="footer-contact">Contact</a>
            <Link to="/brokers/apply" className="hover:text-gold transition-colors" data-testid="footer-broker-apply">For Brokers</Link>
          </div>
          <p className="text-sm text-muted-foreground text-center md:text-right">
            &copy; {new Date().getFullYear()} Assagart. Per Illos Qui Perdurant.
          </p>
        </div>
      </footer>
    </div>
  );
}
